/**
 * <gb-gift-card-claim> — makes the QR code on a legacy gift-card PDF work.
 *
 * The PDFs emailed by the legacy stack carry a QR pointing at
 * `https://giannabellucci.com/<cc>/?coupon=<code>` (api/lp/classes/GiftCard.php
 * createCouponUrl). Those legacy country paths already 301 to the Shopify market
 * locale AND forward the query string — `/sk/?coupon=x` lands on
 * `/sk-sk/?coupon=x` — so the scan reaches the storefront intact. What was
 * missing is anything here that reads the param.
 *
 * Shopify gift cards cannot be applied from a URL: unlike a discount there is no
 * `?discount=`-style parameter, and no cart field either — a card attaches only
 * in checkout. So the most this can honestly do is catch the code, surface it,
 * and make it one tap to paste into the checkout gift-card field.
 *
 * Why the shape test rather than "any ?coupon value": `?coupon=` was the legacy
 * store's parameter for EVERY coupon, marketing codes included, so a stale
 * `?coupon=WELCOME10` link must not announce itself as a gift card. Legacy gift
 * card slugs are exactly 10 lowercase hex characters (`settings.slug`), which
 * WELCOME10 and friends never match. No code is ever embedded here — theme
 * assets are public, and a real card code is a payment credential.
 */
const CODE_RE = /^[0-9a-f]{10}$/;
const STORE_KEY = 'gb_gift_card_code';
const DISMISS_KEY = 'gb_gift_card_dismissed';

/** @param {string} key @returns {string} */
function read(key) {
  try {
    return (localStorage.getItem(key) || '').trim();
  } catch {
    return '';
  }
}

/** @param {string} key @param {string} value */
function write(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode — the banner still works for this page view.
  }
}

class GbGiftCardClaim extends HTMLElement {
  /** @type {string} */
  #code = '';

  connectedCallback() {
    this.#code = this.#claim();
    if (!this.#code) return;
    if (read(DISMISS_KEY) === this.#code) return;

    const slot = this.querySelector('[data-gb-gcc-code]');
    if (slot instanceof HTMLElement) slot.textContent = this.#code;

    this.hidden = false;
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * Returns the code to show: from this page's `?coupon=`, else one caught on an
   * earlier page view. The param is stripped either way — a gift card code has
   * no business sitting in the address bar, in a screenshot, or in the page URL
   * every analytics script on the page reports.
   * @returns {string}
   */
  #claim() {
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get('coupon') || '').trim().toLowerCase();

    if (params.has('coupon')) {
      params.delete('coupon');
      const query = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + (query ? `?${query}` : '') + window.location.hash
      );
    }

    if (CODE_RE.test(raw)) {
      write(STORE_KEY, raw);
      return raw;
    }

    const remembered = read(STORE_KEY);
    return CODE_RE.test(remembered) ? remembered : '';
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const copy = target.closest('[data-gb-gcc-copy]');
    if (copy instanceof HTMLElement) {
      this.#copy(copy);
      return;
    }

    if (target.closest('[data-gb-gcc-close]')) {
      write(DISMISS_KEY, this.#code);
      this.hidden = true;
    }
  };

  /** @param {HTMLElement} button */
  #copy(button) {
    const done = () => {
      const copied = button.dataset.copied;
      if (!copied || button.dataset.busy) return;
      const original = button.textContent;
      button.dataset.busy = 'true';
      button.textContent = copied;
      window.setTimeout(() => {
        button.textContent = original;
        delete button.dataset.busy;
      }, 2000);
    };

    // clipboard.writeText needs a secure context and permission; the textarea
    // fallback keeps the button useful where it is refused.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(this.#code).then(done, () => this.#copyFallback(done));
    } else {
      this.#copyFallback(done);
    }
  }

  /** @param {() => void} done */
  #copyFallback(done) {
    const field = document.createElement('textarea');
    field.value = this.#code;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    try {
      if (document.execCommand('copy')) done();
    } catch {
      // Nothing left to try — the code is on screen to read.
    }
    field.remove();
  }
}

if (!customElements.get('gb-gift-card-claim')) {
  customElements.define('gb-gift-card-claim', GbGiftCardClaim);
}
